import {Component, ElementRef, inject, OnDestroy, OnInit} from "@angular/core";
import {selectIsUndrawnComplete, selectTopDownValuation} from "../../store/fund-valuation/fund-valuation.selectors";
import {Store} from "@ngrx/store";
import {Actions, ofType} from "@ngrx/effects";
import {BehaviorSubject, combineLatest, first, map, Observable, of, Subscription} from "rxjs";
import {FormBuilder, Validators} from "@angular/forms";
import {noWhitespaceValidator, numberNotBiggerThan190000m} from "../../../shared/utils/form-validators";
import {selectIsAnyFundFormInEditMode} from "../../store/fund/fund.selectors";
import {FundValuationActions, TopDownValuationActions} from "../../store/fund.actions";
import {CodeTableEnum} from "../../../shared/model/code";
import {FundValuationScenario} from "../../models/fund-valuation-scenario";
import {sourceIfChanged, TraceableText} from "../../../shared/model/traceable";
import {FundValuation} from "../../models/fund-valuation";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {CalcBaseEnum, TargetEnum} from "../../models/top-down-valuation-calc-request";

@Component({
    selector: "valumize-fund-top-down-valuation",
    standalone: false,
    templateUrl: "./fund-top-down-valuation.component.html",
    styleUrl: "./fund-top-down-valuation.component.scss"
})
export class FundTopDownValuationComponent implements OnInit, OnDestroy {
    subscription?: Subscription;
    private actionsSubscription?: Subscription;
    private saveSubscription?: Subscription;
    fundValuation?: FundValuation;
    scenarios: FundValuationScenario[] = [];
    codeTableScenarioType = CodeTableEnum.SHARED_SCENARIOTYPE;
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);
    private readonly elementRef = inject(ElementRef);
    topDownValuation$ = this.store.select(selectTopDownValuation);
    isUndrawnComplete$ = this.store.select(selectIsUndrawnComplete);
    isEditDisabled$ = this.store.select(selectIsAnyFundFormInEditMode);

    // WI 1077: true from the moment the user triggers a recalc until that recalc settles
    // (calcSuccess/calcError). Local to the component, so it dies with it and never leaks into the
    // store. Drives the Save gate so a Save clicked mid-recalc can't persist pre-recalc data.
    private readonly calcInFlight$ = new BehaviorSubject<boolean>(false);
    // WI 1077: drives the Save button's disabled state + tooltip. Disabled while a recalc is in flight
    // and while the last calc errored. Bound in ngOnInit.
    saveBlock$: Observable<{ disabled: boolean; tooltip: string }> = of({disabled: false, tooltip: ""});

    // WI 1077: navigable grid of the editable inputs (by element id), row-major. The three scalar
    // inputs are single-cell rows above the table's 3×2 grid. Arrow keys move between cells; Tab walks
    // it row-major and escapes natively past the ends.
    private static readonly NAV_GRID: string[][] = [
        ["requiredGrossIRR"],
        ["fundTermExtensionInMonth"],
        ["bidPriceAdjustment"],
        ["totalCashflowLOW", "navMultipleLOW"],
        ["totalCashflowBASE", "navMultipleBASE"],
        ["totalCashflowHIGH", "navMultipleHIGH"]
    ];

    private readonly formBuilder = inject(FormBuilder);
    topDownValuationForm = this.formBuilder.group({
        requiredGrossIRR: this.formBuilder.control<number | null>(null, {
            nonNullable: true,
            // numberNotBiggerThan190000m is the Number()-based numeric guard: min/max use parseFloat and
            // would let "2,5"/"abc"/"12abc" through, which toNumber() (Number()) then silently drops.
            validators: [Validators.required, numberNotBiggerThan190000m(), Validators.min(0), Validators.max(99), noWhitespaceValidator()]
        }),
        fundTermExtensionInMonth: this.formBuilder.control<number>(0, {
            nonNullable: true,
            validators: [Validators.required, numberNotBiggerThan190000m(), Validators.min(0), Validators.max(60), noWhitespaceValidator()]
        }),
        bidPriceAdjustment: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        totalCashflowLOW: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        totalCashflowBASE: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        totalCashflowHIGH: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        navMultipleLOW: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        navMultipleBASE: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]}),
        navMultipleHIGH: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m()]})
    })

    ngOnInit() {
        this.saveBlock$ = combineLatest([this.topDownValuation$, this.calcInFlight$]).pipe(
            map(([topDownValuation, calcInFlight]) =>
                FundTopDownValuationComponent.toSaveBlock(
                    topDownValuation?.fundValuation?.status ?? "",
                    calcInFlight,
                    !!topDownValuation?.tableDatasource?.length))
        );

        // WI 1077: release the in-flight flag once the recalc settles (success or error).
        this.actionsSubscription = this.actions$.pipe(
            ofType(TopDownValuationActions.calcSuccess, TopDownValuationActions.calcError)
        ).subscribe(() => this.calcInFlight$.next(false));

        this.subscription = this.topDownValuation$.subscribe(data => {
            if (data?.fundValuation) {
                this.fundValuation = data.fundValuation.data;
                this.scenarios = data.tableDatasource;

                this.topDownValuationForm.patchValue({
                    requiredGrossIRR: DecimalFormatPipe.transformFractionToPercent(data.fundValuation.data.requiredGrossIRR.fraction) ?? null,
                    fundTermExtensionInMonth: data.fundValuation.data.fundTermExtensionInMonth ?? 0,
                    bidPriceAdjustment: DecimalFormatPipe.transformFromMillionsNum(data.fundValuation.data.bidPriceAdjustment.amount) ?? null,
                    totalCashflowLOW: this.getTotalCashflowInMillion("LOW", data.tableDatasource),
                    totalCashflowBASE: this.getTotalCashflowInMillion("BASE", data.tableDatasource),
                    totalCashflowHIGH: this.getTotalCashflowInMillion("HIGH", data.tableDatasource),
                    navMultipleLOW: this.getNavMultiple("LOW", data.tableDatasource),
                    navMultipleBASE: this.getNavMultiple("BASE", data.tableDatasource),
                    navMultipleHIGH: this.getNavMultiple("HIGH", data.tableDatasource)
                });
            }
        });
    }

    getTotalCashflowInMillion(scenarioCode: string, scenarios: FundValuationScenario[]): number | null {
        return DecimalFormatPipe.transformFromMillionsNum(scenarios.find(s => s.scenario.code === scenarioCode)?.totalCashflow.amount) ?? null;
    }

    getNavMultiple(scenarioCode: string, scenarios: FundValuationScenario[]): number | null {
        return (scenarios.find(s => s.scenario.code === scenarioCode)?.navMultiple.factor) ?? null;
    }

    save() {
        if (this.topDownValuationForm.invalid) {
            return;
        }
        // WI 1077: defer the save until any in-flight recalc has settled, then persist the FRESH store
        // values (the recalculated fundValuation + scenarios). The gate is just !calcInFlight so it ALWAYS
        // settles (it can never wait forever for a particular status), and the persist step only fires when
        // the calculated valuation is LOADED — exactly the condition toSaveBlock() uses to enable the
        // button. So a Save clicked while a recalc runs never persists pre-recalc data, and a click against
        // any non-LOADED state (ERROR / INIT / SAVED / OLD_DATA) aborts cleanly instead of hanging.
        this.saveSubscription?.unsubscribe();
        this.saveSubscription = combineLatest([this.topDownValuation$, this.calcInFlight$]).pipe(
            first(([, calcInFlight]) => !calcInFlight),
            map(([topDownValuation]) => {
                const status = topDownValuation?.fundValuation?.status;
                const fundValuation = topDownValuation?.fundValuation?.data;
                const scenarios = topDownValuation?.tableDatasource;
                if (status !== "LOADED" || this.topDownValuationForm.invalid || !fundValuation || !scenarios?.length) {
                    return;
                }
                this.store.dispatch(FundValuationActions.save({fundValuation, scenarios}));
            })
        ).subscribe();
    }

    calc(formControlName: string) {
        if (!this.fundValuation || this.scenarios.length === 0 || !this.topDownValuationForm.valid) {
            return;
        }

        const fundValuation = this.fundValuationForCalc(this.fundValuation);
        const scenarios = this.scenariosForCalc(this.scenarios);

        const target: TargetEnum = this.getTargetFromControlName(formControlName);
        const calcBase: CalcBaseEnum = this.getCalcBaseFromControlName(formControlName)
        const fundValuationWithScenarios = {fundValuation, scenarios}

        const topDownRequest = {target, calcBase, fundValuationWithScenarios};
        // WI 1077: block Save from now until this recalc settles (covers the in-flight window).
        this.calcInFlight$.next(true);
        this.store.dispatch(TopDownValuationActions.calc({topDownRequest}));
    }

    edit = () => this.store.dispatch(TopDownValuationActions.edit());

    cancel = () => this.store.dispatch(TopDownValuationActions.cancel());

    getAssignedTopDownValuationPattern(scenario: FundValuationScenario): TraceableText {
        return scenario.patterns?.["TOP_DOWN_VALUATION"]?.patternName ?? null;
    }

    // WI 1077: stable row identity (scenario code) so the mat-table reuses each row's view — and its
    // editable <input> cells — across a recalc instead of destroying and recreating them. Without it a
    // recalc emits a fresh tableDatasource array, the rows are rebuilt, and focus on the input the user
    // tabbed to is lost.
    trackByScenarioCode = (_: number, element: FundValuationScenario): string => element.scenario.code ?? "";

    ngOnDestroy() {
        this.subscription?.unsubscribe();
        this.actionsSubscription?.unsubscribe();
        this.saveSubscription?.unsubscribe();
    }

    // WI 1077: map the calc status + in-flight flag to the Save button's disabled state + tooltip. Save is
    // enabled iff a recalc is settled AND the calculated valuation is LOADED — i.e. there is fresh, valid
    // calculated data to persist. This MUST agree with the save() predicate below: anything else (ERROR,
    // and the non-terminal/stale states INIT/LOADING/SAVED/OLD_DATA) disables the button so a click can
    // never become a silent no-op against pre-recalc or empty data.
    private static toSaveBlock(status: string, calcInFlight: boolean, hasScenarios: boolean): { disabled: boolean; tooltip: string } {
        if (calcInFlight) {
            return {disabled: true, tooltip: "Calculating… save is available once the calculation finishes"};
        }
        if (status === "ERROR") {
            return {disabled: true, tooltip: "Last calculation failed — change a value to recalculate before saving"};
        }
        // Mirror exactly what save() will persist: a LOADED valuation with the scenario rows present.
        // Otherwise keep the button disabled so an enabled click can never be a silent no-op.
        if (status !== "LOADED" || !hasScenarios) {
            return {disabled: true, tooltip: "Change a value to calculate a valuation before saving"};
        }
        return {disabled: false, tooltip: ""};
    }

    // WI 1077: Excel-style keyboard navigation across the editable inputs. Arrow keys move between
    // cells, Tab/Shift+Tab walk the grid row-major and escape natively (to the Save button) past the
    // ends. Because the inputs are type="text", arrows no longer drive a native number spinner.
    onGridKeydown(event: KeyboardEvent): void {
        const key = event.key;
        const isArrow = key === "ArrowUp" || key === "ArrowDown" || key === "ArrowLeft" || key === "ArrowRight";
        const isTab = key === "Tab";
        if (!isArrow && !isTab) {
            return;
        }

        const id = (event.target as HTMLElement).getAttribute("id");
        if (!id) {
            return;
        }

        // WI 1077: navigate over the static grid directly — no per-keystroke DOM rebuild. Only the target
        // cell is resolved against the DOM (below), so a keypress costs one querySelector, not ~10.
        const grid = FundTopDownValuationComponent.NAV_GRID;
        let rowIdx = grid.findIndex(row => row.includes(id));
        if (rowIdx < 0) {
            return;
        }
        let colIdx = grid[rowIdx].indexOf(id);

        if (key === "ArrowUp") {
            rowIdx--;
        } else if (key === "ArrowDown") {
            rowIdx++;
        } else if (key === "ArrowLeft") {
            const input = event.target as HTMLInputElement;
            if (input.selectionStart !== 0 || input.selectionStart !== input.selectionEnd) {
                return;
            }
            colIdx--;
        } else if (key === "ArrowRight") {
            const input = event.target as HTMLInputElement;
            const len = input.value?.length ?? 0;
            if (input.selectionEnd !== len || input.selectionStart !== input.selectionEnd) {
                return;
            }
            colIdx++;
        } else if (isTab) {
            const next = event.shiftKey ? this.prevCell(grid, rowIdx, colIdx) : this.nextCell(grid, rowIdx, colIdx);
            if (!next) {
                // Past the last / before the first cell: let Tab escape natively (e.g. to the Save button).
                return;
            }
            [rowIdx, colIdx] = next;
        }

        if (rowIdx < 0 || rowIdx >= grid.length) {
            return;
        }
        // Rows are ragged (scalar rows have one cell); clamp the column when moving between rows.
        if (colIdx < 0 || colIdx >= grid[rowIdx].length) {
            if (key === "ArrowUp" || key === "ArrowDown") {
                colIdx = Math.min(Math.max(colIdx, 0), grid[rowIdx].length - 1);
            } else {
                return;
            }
        }

        const targetInput = this.findInput(grid[rowIdx][colIdx]);
        if (targetInput) {
            event.preventDefault();
            targetInput.focus();
            targetInput.select();
        }
    }

    private findInput(inputId: string): HTMLInputElement | null {
        return this.elementRef.nativeElement.querySelector(`input[id="${inputId}"]`);
    }

    private nextCell(grid: string[][], rowIdx: number, colIdx: number): [number, number] | null {
        if (colIdx + 1 < grid[rowIdx].length) {
            return [rowIdx, colIdx + 1];
        }
        if (rowIdx + 1 < grid.length) {
            return [rowIdx + 1, 0];
        }
        return null;
    }

    private prevCell(grid: string[][], rowIdx: number, colIdx: number): [number, number] | null {
        if (colIdx - 1 >= 0) {
            return [rowIdx, colIdx - 1];
        }
        if (rowIdx - 1 >= 0) {
            return [rowIdx - 1, grid[rowIdx - 1].length - 1];
        }
        return null;
    }

    // WI 1077: the editable inputs are type="text" (so arrow keys don't drive the number spinner), which
    // means the reactive-form controls hold raw STRINGS. Coerce with Number() — the SAME parse the
    // numberNotBiggerThan190000m validator uses — before any numeric use, so (a) numbers (never strings)
    // reach the backend, (b) DecimalFormatPipe's underlying new Big(...) never sees an un-normalized string
    // (e.g. "24 ") and throws, and (c) the dot is always the decimal separator: "1.234"/"2.500" parse to
    // 1.234/2.5, never mis-read as a thousands group. Empty/whitespace and non-numeric input → undefined.
    private toNumber(value: number | string | null): number | undefined {
        if (value == null) {
            return undefined;
        }
        const text = String(value).trim();
        if (text === "") {
            return undefined;
        }
        const parsed = Number(text);
        return isNaN(parsed) ? undefined : parsed;
    }

    private fundValuationForCalc(fundValuation: FundValuation): FundValuation {
        const requiredGrossIRR = DecimalFormatPipe.transformPercentToFraction(this.toNumber(this.topDownValuationForm.controls.requiredGrossIRR.value));
        const fundTermExtensionInMonth = this.toNumber(this.topDownValuationForm.controls.fundTermExtensionInMonth.value);
        const bidPriceAdjustment = DecimalFormatPipe.transformToMillionsNum(this.toNumber(this.topDownValuationForm.controls.bidPriceAdjustment.value));

        return {
            ...fundValuation,
            // US-1238: requiredGrossIRR is pre-filled from the fund.requiredGrossIRR.default setting and
            // stamped "user setting" at valuation creation, which renders the "applied from user settings"
            // gear. Flip it to "user" the moment the user overrides the value, or the spread below would
            // carry "user setting" forward forever: the backend leaves this field untouched (it is a calc
            // INPUT, so TopDownValuationCalcService only re-stamps its derived outputs) and fund valuations
            // are persisted without a TraceableUpdater — so this is the only place the gear can be dropped.
            // Doing it here (not in save()) means the calc response carries "user" back into the store, so
            // the gear disappears as soon as the recalc settles rather than only after Save.
            requiredGrossIRR: {
                ...fundValuation.requiredGrossIRR,
                fraction: requiredGrossIRR,
                source: sourceIfChanged(requiredGrossIRR !== fundValuation.requiredGrossIRR.fraction, fundValuation.requiredGrossIRR.source)
            },
            fundTermExtensionInMonth,
            bidPriceAdjustment: {
                ...fundValuation.bidPriceAdjustment,
                amount: bidPriceAdjustment
            },
        };
    }

    private scenariosForCalc(scenarios: FundValuationScenario[]): FundValuationScenario[] {
        const controls = this.topDownValuationForm.controls;
        const lowScenario = scenarios.find(s => s.scenario.code === "LOW");
        const baseScenario = scenarios.find(s => s.scenario.code === "BASE");
        const highScenario = scenarios.find(s => s.scenario.code === "HIGH");

        const updatedScenarios: FundValuationScenario[] = [];
        if (lowScenario) {
            const updatedLowScenario = this.updateScenario(
                lowScenario,
                this.toNumber(controls.navMultipleLOW.value),
                DecimalFormatPipe.transformToMillionsNum(this.toNumber(controls.totalCashflowLOW.value)) ?? undefined
            );
            updatedScenarios.push(updatedLowScenario);
        }
        if (baseScenario) {
            const updatedBaseScenario = this.updateScenario(
                baseScenario,
                this.toNumber(controls.navMultipleBASE.value),
                DecimalFormatPipe.transformToMillionsNum(this.toNumber(controls.totalCashflowBASE.value)) ?? undefined
            );
            updatedScenarios.push(updatedBaseScenario);
        }
        if (highScenario) {
            const updatedHighScenario = this.updateScenario(
                highScenario,
                this.toNumber(controls.navMultipleHIGH.value),
                DecimalFormatPipe.transformToMillionsNum(this.toNumber(controls.totalCashflowHIGH.value)) ?? undefined
            );
            updatedScenarios.push(updatedHighScenario);
        }
        return updatedScenarios;
    }

    private updateScenario(scenario: FundValuationScenario, navMultiple?: number, totalCashflow?: number): FundValuationScenario {
        return {
            ...scenario,
            navMultiple: {
                ...scenario.navMultiple,
                factor: navMultiple
            },
            totalCashflow: {
                ...scenario.totalCashflow,
                amount: totalCashflow
            }
        };
    }

    private getTargetFromControlName(controlName: string): TargetEnum {
        const regex = /(LOW|BASE|HIGH)$/;
        const result = regex.exec(controlName);
        return (result?.[1] as TargetEnum) ?? "VALUATION";
    }

    private getCalcBaseFromControlName(controlName: string): CalcBaseEnum {
        if (controlName.startsWith("totalCashflow")) {
            return "TOTAL_CASHFLOW";
        }
        if (controlName.startsWith("navMultiple")) {
            return "NAV_MULTIPLE";
        }
        return "AUTO";
    }
}
